import type { Scan, ScanReviewGroup } from "@hobbyshopify/backend-client";
import { Rx, RxList } from "subtree.js/core";
import { idleLoad, type LoadStatus } from "../../core/primitives/load-status";
import type { ReviewDraft } from "../../core/primitives/review-row";
import type { BulkCounts, BulkItem } from "../../core/services/bulk-scan-queue";

/** The `busyKey` while "Confirm all priced" runs. */
export const CONFIRM_ALL_KEY = "__priced__";

const sameCounts = (a: BulkCounts, b: BulkCounts) =>
  a.queued === b.queued && a.scanning === b.scanning && a.error === b.error;

export class DraftsState {
  readonly status = new Rx<LoadStatus>(idleLoad);
  readonly groups = new RxList<ScanReviewGroup>();
  readonly unrecognised = new RxList<Scan>();
  readonly actionError = new Rx<string | null>(null);
  /** The group a confirm or reject is running for, or `CONFIRM_ALL_KEY`; null when idle. */
  readonly busyKey = new Rx<string | null>(null);
  /** What the reviewer typed on each row, by group key. */
  readonly drafts = new Rx<Readonly<Record<string, ReviewDraft>>>({});
  /** The candidate a pick is running for. */
  readonly picking = new Rx<{ key: string; productId: string } | null>(null);
  readonly bulkOpen: Rx<boolean>;
  // The bulk queue, mirrored from the service.
  readonly bulkItems = new RxList<BulkItem>();
  readonly bulkCounts = new Rx<BulkCounts>({ queued: 0, scanning: 0, error: 0 }, sameCounts);
  readonly bulkRunning = new Rx(false);
  readonly bulkPaused = new Rx(false);

  constructor(bulkOpen: boolean) {
    this.bulkOpen = new Rx(bulkOpen);
  }
}

export abstract class DraftsActions {
  abstract toggleBulk(): void;
  abstract confirm(group: ScanReviewGroup): Promise<void>;
  abstract reject(group: ScanReviewGroup): Promise<void>;
  abstract review(group: ScanReviewGroup): void;
  /** Confirms, one after another, the catalogued rows and every row whose price was typed; customers' scans never. */
  abstract confirmAllPriced(): Promise<void>;
  abstract setPrice(group: ScanReviewGroup, price: string): void;
  abstract setQuantity(group: ScanReviewGroup, quantity: number): void;
  abstract pick(group: ScanReviewGroup, productId: string): Promise<void>;
  abstract dismissError(): void;
  abstract retry(): void;
  abstract addFiles(files: Iterable<File> | null): void;
  abstract stopBulk(): void;
  abstract resumeBulk(): void;
  abstract retryItem(id: string): void;
  abstract removeItem(id: string): void;
}
